import { Duration } from 'luxon'

import { mergeBuffers } from '@/common/utils/buffer/mergeBuffers'
import { noOp } from '@/utils/helpers'
import logger from '@/utils/logger'

import RPC from '@/client/rpc'

import {
  ProviderType,
  type ReceiveTranscriptFragment,
  type SpeechRecognitionAction,
  type TranscriptFragment,
} from './types'

// AssemblyAI WS Response Types: https://www.assemblyai.com/docs/api-reference/streaming-api/streaming-api
// "Begin" and "Termination" types are not used here
interface AssemblyAITranscriptionEvent {
  type: 'Turn' // A "Turn" is a block of speech from a single speaker
  turn_order: number
  end_of_turn: boolean // Is this transcript of the turn finalized, or could it change?
  end_of_turn_confidence: number
  transcript: string
  turn_is_formatted: boolean // Is this transcript of the turn formatted, or is it raw words
  words: {
    text: string
    start: number
    end: number
    confidence: number
    word_is_final: boolean
  }[]
}

const ASSEMBLY_AI_SAMPLE_RATE = 16000
const ASSEMBLY_AI_CHUNK_SIZE = Duration.fromMillis(50)
const ASSEMBLY_AI_LATENCY_HINT = 'balanced'
const ASSEMBLY_AI_FORMATTED_FINALS = true

const getAssemblyAIUrl = (token: string) =>
  `wss://streaming.assemblyai.com/v3/ws?sample_rate=${ASSEMBLY_AI_SAMPLE_RATE}&formatted_finals=${ASSEMBLY_AI_FORMATTED_FINALS}&token=${token}`

// Common lifecycle states with AssemblyAI-specific resources
export type AssemblyAIState =
  | {
      type: 'idle'
      provider: ProviderType.AssemblyAI
    }
  | {
      type: 'session-ready'
      provider: ProviderType.AssemblyAI
      micStream: MediaStream
    }
  | {
      type: 'recording'
      provider: ProviderType.AssemblyAI
      micStream: MediaStream
      audioContext: AudioContext
      processor: AudioWorkletNode
      source: MediaStreamAudioSourceNode
      websocket: WebSocket
      audioBufferQueue: Int16Array
      onTranscriptFragment: ReceiveTranscriptFragment
    }
  | {
      type: 'error'
      provider: ProviderType.AssemblyAI
      error: string
    }

export async function assemblyAITransitionToState(
  currentState: AssemblyAIState,
  action: SpeechRecognitionAction,
  onError: (error: string) => void
): Promise<AssemblyAIState> {
  // Handle error - transition to error state from any state
  if (action.type === 'error-occurred') {
    return {
      type: 'error',
      error: action.error,
      provider: ProviderType.AssemblyAI,
    }
  }

  // Handle reset - clean up all resources from any state
  if (action.type === 'reset') {
    if ('websocket' in currentState) {
      currentState.websocket.close()
    }
    if ('audioContext' in currentState) {
      // Automatically closes 'source' and 'processor' as well
      currentState.audioContext.close().catch(noOp)
    }
    if ('micStream' in currentState) {
      currentState.micStream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop()
      })
    }

    return {
      type: 'idle',
      provider: ProviderType.AssemblyAI,
    }
  }

  // Handle start-session action
  if (action.type === 'start-session') {
    if (currentState.type !== 'idle') {
      // Invalid state transition - start-session only valid from idle state
      return {
        type: 'error',
        provider: ProviderType.AssemblyAI,
        error: `Cannot transition from ${currentState.type} state to start-session`,
      }
    }

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true })

      return {
        type: 'session-ready',
        provider: ProviderType.AssemblyAI,
        micStream,
      }
    } catch (error) {
      return {
        type: 'error',
        provider: ProviderType.AssemblyAI,
        error: error.message,
      }
    }
  }

  // Handle start-recording action
  if (action.type === 'start-recording') {
    if (currentState.type !== 'session-ready') {
      // Invalid state transition - start-recording only valid from session-ready state
      return {
        type: 'error',
        provider: ProviderType.AssemblyAI,
        error: `Cannot transition from ${currentState.type} state to start-recording`,
      }
    }

    try {
      const { websocket, processor, source, audioContext } = await setupAudioFixtures(
        currentState,
        action.onTranscriptFragment,
        onError
      )

      return {
        type: 'recording',
        provider: ProviderType.AssemblyAI,
        micStream: currentState.micStream,
        audioContext,
        processor,
        source,
        websocket,
        audioBufferQueue: new Int16Array(0),
        onTranscriptFragment: action.onTranscriptFragment,
      }
    } catch (error) {
      return {
        type: 'error',
        provider: ProviderType.AssemblyAI,
        error: `Failed to start AssemblyAI recording: ${error.message}`,
      }
    }
  }

  // Handle stop-recording action
  if (action.type === 'stop-recording') {
    if (currentState.type !== 'recording') {
      // Invalid state transition - stop-recording only valid from recording state
      return {
        type: 'error',
        provider: ProviderType.AssemblyAI,
        error: `Cannot transition from ${currentState.type} state to stop-recording`,
      }
    }

    // Clean up recording resources but keep session
    currentState.processor?.disconnect()
    currentState.source?.disconnect()
    currentState.websocket?.close()
    await currentState.audioContext?.close()

    return {
      type: 'session-ready',
      provider: ProviderType.AssemblyAI,
      micStream: currentState.micStream,
    }
  }

  // Assert all action types have been handled
  action satisfies never
  return currentState
}

async function initializeAssemblyAISocket(
  onTranscriptFragment: ReceiveTranscriptFragment,
  onError: (error: string) => void
): Promise<WebSocket> {
  const token = await RPC.getAssemblyAIEphemeralToken({})
  const ws = new WebSocket(getAssemblyAIUrl(token))

  ws.onmessage = event => {
    let data: AssemblyAITranscriptionEvent
    try {
      data = JSON.parse(event.data)
    } catch {
      logger.error('WebSocket message is not valid JSON', { event })
      onError('Failed to parse WebSocket message as JSON')
      return
    }
    if (data.type === 'Turn') {
      const fragment = convertToTranscriptFragment(data)
      onTranscriptFragment(fragment)
    }
  }

  ws.onerror = event => {
    onError(`WebSocket error: ${event}`)
  }

  return ws
}

async function setupAudioFixtures(
  state: Extract<AssemblyAIState, { type: 'session-ready' }>,
  onTranscriptFragment: ReceiveTranscriptFragment,
  onError: (error: string) => void
): Promise<{
  websocket: WebSocket
  processor: AudioWorkletNode
  source: MediaStreamAudioSourceNode
  audioContext: AudioContext
}> {
  const audioContext = new AudioContext({
    sampleRate: ASSEMBLY_AI_SAMPLE_RATE,
    latencyHint: ASSEMBLY_AI_LATENCY_HINT,
  })

  // We initialize the WebSocket here because we are billed by socket session duration
  const websocket = await initializeAssemblyAISocket(onTranscriptFragment, onError)

  await audioContext.audioWorklet.addModule(new URL('./audio-processor', import.meta.url))
  const processor = new AudioWorkletNode(audioContext, 'audio-processor')

  const source = audioContext.createMediaStreamSource(state.micStream)
  source.connect(processor)
  processor.connect(audioContext.destination)

  // Create a reference to the recording state that will be shared across callbacks
  const recordingState: Extract<AssemblyAIState, { type: 'recording' }> = {
    type: 'recording',
    provider: ProviderType.AssemblyAI,
    micStream: state.micStream,
    audioContext,
    processor,
    source,
    websocket,
    audioBufferQueue: new Int16Array(0),
    onTranscriptFragment,
  }

  // Audio processor (WebWorker) receives stream data, encodes it, and sends to main thread
  processor.port.onmessage = event => {
    // Use the shared state reference so buffer persists between callbacks
    sendEncodedAudioToAssemblyAI(event, recordingState)
  }

  return { websocket, processor, source, audioContext }
}

function sendEncodedAudioToAssemblyAI(event: MessageEvent, state: AssemblyAIState) {
  if (state.type !== 'recording') {
    return
  }

  // Concatenate this audio to any left over
  const currentBuffer = new Int16Array(event.data.audio_data)
  const newQueue = mergeBuffers(state.audioBufferQueue, currentBuffer)

  state.audioBufferQueue = newQueue

  const bufferDuration = Duration.fromObject({
    seconds: newQueue.length / state.audioContext.sampleRate,
  })

  // Send audio chunks of at least ASSEMBLY_AI_CHUNK_SIZE (50ms) through socket
  if (bufferDuration >= ASSEMBLY_AI_CHUNK_SIZE) {
    const totalSamples = Math.floor(
      state.audioContext.sampleRate * ASSEMBLY_AI_CHUNK_SIZE.as('seconds')
    )
    const finalBuffer = new Uint8Array(newQueue.subarray(0, totalSamples).buffer)

    // Update the state's buffer queue with remaining samples
    state.audioBufferQueue = newQueue.subarray(totalSamples)

    if (state.websocket.readyState === WebSocket.OPEN) {
      state.websocket.send(finalBuffer)
    }
  }
}

function convertToTranscriptFragment(data: AssemblyAITranscriptionEvent): TranscriptFragment {
  return {
    turn: data.turn_order,
    isFinal: data.end_of_turn,
    isFormatted: data.turn_is_formatted,
    content: data.transcript,
    words: data.words.map(word => ({
      text: word.text,
      isFinal: word.word_is_final,
    })),
  }
}
